import type { ContentPage } from "../types";

// Facts about the rename, and where each one comes from, so they can be
// re-checked:
// - voicify.ai and www.voicify.ai answer 301 to https://www.jammable.com/,
//   keeping the path (/models, /pricing, /train, /login): checked with curl
//   on 2026-10-17.
// - The in-app announcement (components/modals/RebrandModal.js): "Voicify AI
//   is now Jammable!", the reason, and "All your account information and
//   history will remain".
// - Company name: pages/terms/2023-07-12.tsx (last updated 21/12/2023) names
//   Voicify AI Ltd; pages/terms/index.tsx (19/11/2024) and PageFooter.js
//   name Jammable Limited. pages/_document.js lists "Voicify AI" as the
//   Organization's alternateName.
// - No rename date is given: the git history here is shallow (Oct 2026) and
//   nothing in the repo states the day. Add one only from a primary source.
// Plans and features: utils/pricing.ts PRICE_MAP and /pricing.md (USD).
// Locked covers are held 48 hours: utils/lockedCover.ts LOCKED_HOLD_MS.
const page: ContentPage = {
  section: "blog",
  slug: "voicify-ai-is-now-jammable",
  coverLabel: "Voicify → Jammable",
  title: "Voicify AI Is Now Jammable: What Changed and How to Log In",
  metaDescription:
    "Voicify AI renamed itself Jammable. voicify.ai now redirects to jammable.com, and your account, voices and covers carried over. Here is how to log in.",
  h1: "Voicify AI is now Jammable",
  kind: "Guide",
  stats: [
    {
      value: "301",
      label: "permanent redirect from voicify.ai to jammable.com",
    },
    { value: "60,000+", label: "community voices on Jammable today" },
    { value: "$1.99", label: "first month on Basic, then $8.99/mo" },
  ],
  answer:
    "Voicify AI is now called Jammable. It is the same AI cover service under a new name: voicify.ai redirects to jammable.com, and your Voicify account, voices and covers carried over. Log in at jammable.com with the same Google account or email and password. Covers need a plan, from $1.99 for the first month.",
  takeaways: [
    "Voicify AI and Jammable are the same service; only the name and the web address changed.",
    "Every voicify.ai link redirects to the same page on jammable.com.",
    "Your old login works: same Google account, or same email and password.",
    "Free accounts can browse voices and set up a cover; making covers needs a plan.",
  ],
  queries: [
    "voicify ai",
    "voicify",
    "voicify ai free",
    "what happened to voicify ai",
    "is voicify ai the same as jammable",
    "voicify ai login",
  ],
  published: "2026-10-17",
  updated: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What happened to Voicify AI?

Voicify AI changed its name to Jammable. The service did not shut down: the voice library, the cover maker, voice training and your account all moved to the new name. Jammable's in-app announcement said "Voicify AI is now Jammable!" and explained why: the rebrand marks a move away from being just an "AI voice platform", as Jammable explores and offers new experiences for its users.

The same announcement promised that "all your account information and history will remain", and that is how it works today. If you made covers or trained voices on Voicify AI, they are in your Jammable account.

## Is Voicify AI the same as Jammable?

Yes. Jammable is what Voicify AI is called now. Jammable's own site lists "Voicify AI" as its former name, and the company named in the terms of use went from Voicify AI Ltd in an older version to Jammable Limited in the current one.

You can check the redirect yourself: open voicify.ai and your browser lands on jammable.com. The old addresses keep their path, so an old link to the Voicify voice library opens the Jammable voice library, an old pricing link opens [Jammable pricing](/pricing), and so on.`,
    },
    {
      type: "table",
      heading: "What changed and what stayed the same",
      table: {
        caption: "Checked against voicify.ai and jammable.com on 2026-10-17",
        columns: ["", "Voicify AI", "Jammable"],
        rows: [
          ["Name", "Voicify AI", "Jammable"],
          ["Website", "voicify.ai", "jammable.com (voicify.ai redirects here)"],
          ["Company in the terms of use", "Voicify AI Ltd", "Jammable Limited"],
          ["Your account and login", "Your account", "The same account"],
          [
            "Your voices and covers",
            "Saved to your account",
            "Still in your account, in your Library",
          ],
        ],
      },
    },
    {
      type: "steps",
      heading: "How to log in with your old Voicify AI account",
      items: [
        {
          title: "Go to jammable.com",
          body: "Type jammable.com, or open an old voicify.ai bookmark: it sends you to the same page on Jammable. There is no separate Voicify AI login any more.",
        },
        {
          title: "Choose Log in",
          body: "Use the log in button at the top of the page. You don't need to make a new account.",
        },
        {
          title: "Use the same method as before",
          body: "If you signed up to Voicify AI with Google, press **Continue with Google** and pick the same Google account. If you used an email and password, enter the same ones.",
        },
        {
          title: "Reset your password if you forgot it",
          body: "Press **Forgot password?** on the log in page and enter the email you signed up with. Jammable emails you a link to set a new password.",
        },
        {
          title: "Find your voices and covers",
          body: "Open your [Library](/library) to see the voices you trained and the covers you made under the Voicify AI name.",
        },
      ],
    },
    {
      type: "callout",
      tone: "tip",
      title: "Can't find your account?",
      text: "Try the other sign-in method. If you signed up with Continue with Google, your account may have no password, so log in with Google instead. If neither works, use **Forgot password?** with each email address you might have used.",
    },
    {
      type: "md",
      text: `## Is Voicify AI still free?

Voicify AI is now Jammable, so the question is what Jammable lets you do for free. A free account can:

- browse all 60,000+ voices in the [voice library](/models) and listen to their demos,
- search by name and filter by category, such as anime, cartoon, gaming and music,
- set up a cover: pick a voice, add audio and choose the settings.

Making the cover needs a plan. If you press Create on a free account, Jammable saves the cover you set up and holds it for 48 hours, so you can make it as soon as you pick a plan without setting it up again.

Every plan includes unlimited covers. The cheapest is Basic at $1.99 for the first month, then $8.99 a month.`,
    },
    {
      type: "matrix",
      heading: "Jammable plans compared",
      matrix: {
        caption:
          "Monthly prices in USD. Checkout uses your local currency where it can.",
        tools: ["Free account", "Basic", "Creator", "Power"],
        rows: [
          {
            feature: "Price",
            values: [
              "$0",
              "$1.99 first month, then $8.99/mo",
              "$9.99 first month, then $24.99/mo",
              "$89.99/mo",
            ],
          },
          {
            feature: "Browse voices and listen to demos",
            values: ["yes", "yes", "yes", "yes"],
          },
          {
            feature: "Set up a cover",
            values: ["yes", "yes", "yes", "yes"],
          },
          {
            feature: "Make covers",
            values: ["no", "Unlimited", "Unlimited", "Unlimited"],
          },
          {
            feature: "Text-to-speech and duets",
            values: ["no", "yes", "yes", "yes"],
          },
          {
            feature: "Train your own voices",
            values: ["no", "no", "Unlimited", "Unlimited"],
          },
          {
            feature: "Upload RVC models (.pth + .index)",
            values: ["no", "no", "yes", "yes"],
          },
          {
            feature: "Shareable videos",
            values: ["no", "no", "yes", "yes"],
          },
          {
            feature: "Unlimited video coins and API credits",
            values: ["no", "no", "no", "yes"],
          },
        ],
      },
    },
    {
      type: "md",
      text: `Plans renew monthly and you can cancel at any time from your account settings; you keep your plan until the end of the month you paid for. Credits never expire. See [pricing](/pricing) for the prices in your currency.

## What you can do on Jammable today

If you last used Voicify AI a while ago, this is what the service does now:

- **AI covers.** Pick a voice, add audio and Jammable re-sings the vocal in that voice. You can change the pitch (try +12 semitones going from a male to a female voice), add autotune, set the vocal volume and split out backing vocals. Start on the [cover maker](/create).
- **Duets.** Put up to 6 voices in one cover and let Jammable switch between them where the singing changes, or choose who sings each part yourself.
- **Text-to-speech in any voice.** Type up to 2,000 characters and hear them read in the voice you picked, with a choice of accent and delivery.
- **AI songs.** Describe a style, write lyrics or let AI write them, and have a voice sing the result.
- **Train your own voice.** Upload or record at least 3 minutes of your own voice, or a voice whose owner has agreed, and a 10-minute dataset is usually ready in about 25 minutes. See [how to train your own AI voice model](/blog/how-to-train-an-ai-voice-model).
- **Bring your own model.** Upload RVC models you trained elsewhere. If you are coming from another tool, see our [Weights AI alternative](/weights-gg-alternative) page.
- **Developer API and MCP server.** Power includes $25 of API credits a month for the [Jammable API](/developers) and the [MCP server](/mcp) for AI agents.

Jammable runs in the browser on any device, with nothing to install. It does not change your voice live, so it isn't a tool for calls or streams; for changing the voice on a recording, see the [AI voice changer](/ai-voice-changer).`,
    },
    {
      type: "verdicts",
      heading: "What should I do with my old Voicify AI account?",
      items: [
        {
          useCase: "You just want to make covers again",
          pick: "Log in and choose Basic",
          why: "Your account is already there. Basic has unlimited covers from $1.99 for the first month.",
        },
        {
          useCase: "You trained voices on Voicify AI",
          pick: "Check your Library",
          why: "Your account history carried over, so voices you trained before the rename are in your Library, ready to pick on the cover maker.",
        },
        {
          useCase: "You want to train new voices",
          pick: "Creator",
          why: "Creator includes unlimited voice training and shareable videos.",
        },
        {
          useCase: "You want to compare first",
          pick: "Read the comparison",
          why: "Our dated comparison of AI cover generators lists prices, voices and free plans side by side.",
        },
      ],
    },
    {
      type: "cta",
      href: "/models",
      label: "Browse voices",
      text: "Same service, new name. Find a voice among 60,000+ and pick up where you left off.",
    },
    {
      type: "md",
      text: `## Still deciding?

Our [comparison of the best AI cover generators](/compare/best-ai-cover-generators) puts Jammable next to the other tools on voices, training, free plans and price, and says where another tool does something better.`,
    },
  ],
  faqs: [
    {
      q: "What happened to Voicify AI?",
      a: "Voicify AI changed its name to Jammable. It is the same service: the voice library, the cover maker and voice training all moved to jammable.com, and voicify.ai redirects there.",
    },
    {
      q: "Is Voicify AI the same as Jammable?",
      a: "Yes. Jammable is Voicify AI's new name. Your Voicify AI account, voices and covers are part of Jammable, and the company named in the terms of use is now Jammable Limited.",
    },
    {
      q: "How do I log in to Voicify AI?",
      a: "Go to jammable.com and log in with the same method you used on Voicify AI: Continue with Google with the same Google account, or the same email and password. If you forgot your password, use Forgot password? on the log in page.",
    },
    {
      q: "Is Voicify AI free?",
      a: "Voicify AI is now Jammable. A free Jammable account can browse every voice, listen to demos and set up a cover, but making covers needs a plan. Basic costs $1.99 for the first month, then $8.99 a month, with unlimited covers.",
    },
    {
      q: "Does voicify.ai still work?",
      a: "voicify.ai redirects to jammable.com and keeps the rest of the address, so old links to voices, pricing or training land on the same page on Jammable.",
    },
    {
      q: "Are my old Voicify AI voices and covers still there?",
      a: "Yes. Account information and history carried over to Jammable. Log in and open your Library to find the voices you trained and the covers you made.",
    },
    {
      q: "Why did Voicify AI change its name?",
      a: "Jammable's announcement said the rebrand marks a move away from being just an AI voice platform, as it explores and offers new experiences for its users.",
    },
  ],
  related: [
    { label: "Browse 60,000+ AI voices", href: "/models" },
    { label: "Make a cover", href: "/create" },
    { label: "Pricing", href: "/pricing" },
    {
      label: "Best AI cover generators",
      href: "/compare/best-ai-cover-generators",
    },
    {
      label: "Train your own voice",
      href: "/blog/how-to-train-an-ai-voice-model",
    },
  ],
};

export default page;
